import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Settings, ArrowLeft } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import inventoryService from '../services/inventoryService';
import { renderMultiValueField } from '../utils/fieldDisplayUtils';

// Componente Switch personalizado
interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  className?: string;
}

const Switch: React.FC<SwitchProps> = ({ checked, onChange, disabled = false, className = '' }) => {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      className={`${
        checked ? 'bg-blue-600' : 'bg-gray-200'
      } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
        disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
      } ${className}`}
    >
      <span
        className={`${
          checked ? 'translate-x-6' : 'translate-x-1'
        } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
      />
    </button>
  );
};

interface CategoryManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface Category {
  id: string;
  name: string;
  fields: Record<string, any>;
  enabled: boolean;
}

const CategoryManagementModal: React.FC<CategoryManagementModalProps> = ({ isOpen, onClose }) => {
  const { isDark } = useTheme();
  const { user } = useAuth();
  const { t } = useI18n();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [fieldSettings, setFieldSettings] = useState<Record<string, Record<string, boolean>>>({});

  const loadCategories = async () => {
    setLoading(true);
    try {
      const categoriesData = await inventoryService.getCategories();
      setCategories(categoriesData);
      
      // Inicializar configuración de campos (todos obligatorios por defecto)
      const initialFieldSettings: Record<string, Record<string, boolean>> = {};
      categoriesData.forEach((category: any) => {
        initialFieldSettings[category.name] = {};
        Object.keys(category.fields || {}).forEach(fieldName => {
          // Los campos globales siempre son obligatorios
          const globalFields = ['sku', 'stock', 'precioCompra', 'precioVenta', 'fechaIngreso', 'marca', 'proveedor', 'genero', 'temporada'];
          initialFieldSettings[category.name][fieldName] = globalFields.includes(fieldName) || true;
        });
      });
      setFieldSettings(initialFieldSettings);
      
    } catch (error) {
      console.error('Error cargando categorías:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && user) {
      loadCategories();
    }
  }, [isOpen, user]);

  const handleCloseModal = () => {
    setSelectedCategory(null);
    onClose();
  };

  const handleFieldToggle = (categoryName: string, fieldName: string) => {
    setFieldSettings(prev => ({
      ...prev,
      [categoryName]: {
        ...prev[categoryName],
        [fieldName]: !prev[categoryName]?.[fieldName]
      }
    }));
  };

  const renderFieldValue = (fieldName: string, fieldValue: any) => {
    if (typeof fieldValue === 'object' && fieldValue !== null && !Array.isArray(fieldValue)) {
      if (fieldValue.type && fieldValue.options) {
        return (
          <div className="space-y-1">
            <span className={`inline-block px-2 py-1 rounded-md text-xs ${
              isDark ? 'bg-blue-900 text-blue-200' : 'bg-blue-100 text-blue-800'
            }`}>
              {fieldValue.type}
            </span>
            {fieldValue.options && Array.isArray(fieldValue.options) && (
              <div className="text-xs text-gray-500">
                {fieldValue.options.length} opciones
              </div>
            )}
          </div>
        );
      }
      return (
        <span className={`inline-block px-2 py-1 rounded-md text-xs ${
          isDark ? 'bg-slate-700 text-slate-300' : 'bg-gray-100 text-gray-700'
        }`}>
          {JSON.stringify(fieldValue)}
        </span>
      );
    }
    
    if (Array.isArray(fieldValue)) {
      // Usar la función utilitaria para campos multi-valor
      return renderMultiValueField(fieldValue, fieldName);
    }
    
    return (
      <span className={`inline-block px-2 py-1 rounded-md text-xs mr-1 mb-1 ${
        isDark ? 'bg-slate-700 text-slate-300' : 'bg-gray-100 text-gray-700'
      }`}>
        {String(fieldValue)}
      </span>
    );
  };

  const selectedCategoryData = categories.find(cat => cat.name === selectedCategory);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50"
            onClick={handleCloseModal}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className={`fixed inset-4 z-50 rounded-2xl shadow-2xl ${
              isDark ? 'bg-slate-900' : 'bg-white'
            }`}
          >
            <div className="h-full flex flex-col">
              {/* Header */}
              <div className={`flex items-center justify-between p-6 border-b ${
                isDark ? 'border-slate-700' : 'border-gray-200'
              }`}>
                <div className="flex items-center space-x-3">
                  {selectedCategory && (
                    <button
                      onClick={() => setSelectedCategory(null)}
                      className={`p-2 rounded-lg ${
                        isDark ? 'hover:bg-slate-800' : 'hover:bg-gray-100'
                      }`}
                    >
                      <ArrowLeft className={`w-5 h-5 ${isDark ? 'text-white' : 'text-gray-700'}`} />
                    </button>
                  )}
                  <Settings className={`w-6 h-6 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                  <h2 className={`text-2xl font-bold ${
                    isDark ? 'text-white' : 'text-gray-900'
                  }`}>
                    {selectedCategory ? `Editar ${selectedCategory}` : t('categoryConfiguration')}
                  </h2>
                </div>
                <button
                  onClick={handleCloseModal}
                  className={`p-2 rounded-lg ${
                    isDark ? 'hover:bg-slate-800' : 'hover:bg-gray-100'
                  }`}
                >
                  <X className={`w-6 h-6 ${isDark ? 'text-white' : 'text-gray-700'}`} />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-hidden">
                {loading ? (
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                      <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                        Cargando categorías...
                      </p>
                    </div>
                  </div>
                ) : selectedCategory ? (
                  // Vista de edición de campos
                  <div className="h-full overflow-y-auto p-6">
                    <div className="max-w-4xl mx-auto">
                      <div className="mb-6">
                        <h3 className={`text-lg font-semibold mb-2 ${
                          isDark ? 'text-white' : 'text-gray-900'
                        }`}>
                          Campos de {selectedCategory}
                        </h3>
                        <p className={`text-sm ${
                          isDark ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                          Configura qué campos son obligatorios al crear productos de esta categoría
                        </p>
                      </div>

                      <div className="space-y-4">
                        {selectedCategoryData && Object.entries(selectedCategoryData.fields || {}).map(([fieldName, fieldValue]) => {
                          const isRequired = fieldSettings[selectedCategory]?.[fieldName] ?? true;
                          const isGlobalField = ['sku', 'stock', 'precioCompra', 'precioVenta', 'fechaIngreso', 'marca', 'proveedor', 'genero', 'temporada'].includes(fieldName);
                          
                          return (
                            <div key={fieldName} className={`p-4 rounded-xl border ${
                              isDark ? 'bg-slate-800 border-slate-700' : 'bg-gray-50 border-gray-200'
                            }`}>
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center space-x-3 mb-2">
                                    <h4 className={`font-medium ${
                                      isDark ? 'text-white' : 'text-gray-900'
                                    }`}>
                                      {fieldName}
                                    </h4>
                                    {isGlobalField && (
                                      <span className={`px-2 py-1 rounded-md text-xs ${
                                        isDark ? 'bg-green-900 text-green-200' : 'bg-green-100 text-green-800'
                                      }`}>
                                        Global
                                      </span>
                                    )}
                                    {isRequired && (
                                      <span className={`px-2 py-1 rounded-md text-xs ${
                                        isDark ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-800'
                                      }`}>
                                        Obligatorio
                                      </span>
                                    )}
                                  </div>
                                  <div className="mb-2">
                                    {renderFieldValue(fieldName, fieldValue)}
                                  </div>
                                </div>
                                <div className="ml-4">
                                  <Switch
                                    checked={isRequired}
                                    onChange={() => handleFieldToggle(selectedCategory, fieldName)}
                                    disabled={isGlobalField}
                                  />
                                </div>
                              </div>
                              {isGlobalField && (
                                <p className={`text-xs mt-2 ${
                                  isDark ? 'text-gray-400' : 'text-gray-500'
                                }`}>
                                  Los campos globales siempre son obligatorios
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ) : (
                  // Vista de lista de categorías
                  <div className="h-full overflow-y-auto p-6">
                    <div className="max-w-4xl mx-auto">
                      <div className="mb-6">
                        <h3 className={`text-lg font-semibold mb-2 ${
                          isDark ? 'text-white' : 'text-gray-900'
                        }`}>
                          {t('availableCategories')}
                        </h3>
                        <p className={`text-sm ${
                          isDark ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                          {t('selectCategoryToConfigure')}
                        </p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {categories.map((category) => (
                          <div
                            key={category.id}
                            onClick={() => setSelectedCategory(category.name)}
                            className={`p-4 rounded-xl border cursor-pointer transition-all hover:shadow-lg ${
                              isDark 
                                ? 'bg-slate-800 border-slate-700 hover:bg-slate-700' 
                                : 'bg-white border-gray-200 hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <h4 className={`font-medium ${
                                isDark ? 'text-white' : 'text-gray-900'
                              }`}>
                                {category.name}
                              </h4>
                              <Settings className={`w-4 h-4 ${
                                isDark ? 'text-gray-400' : 'text-gray-500'
                              }`} />
                            </div>
                            <p className={`text-sm ${
                              isDark ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                              {Object.keys(category.fields || {}).length} {t('fields')}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CategoryManagementModal;